/**
 * UserProfilePage - View user profiles and their shared presets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ChevronLeft,
  User,
  Star,
  Download,
  ArrowUp,
  Music,
  Sliders,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  TrendingUp,
  Clock,
  Filter,
  Grid,
  List,
  Edit,
  Sparkles,
  Award,
  Heart,
  ChevronRight,
  Loader2,
  X,
  Save,
  Camera,
  ShoppingBag,
  DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Edit Profile Modal Component
const EditProfileModal = ({ isOpen, onClose, profile, onSave }) => {
  const [displayName, setDisplayName] = useState(profile?.display_name || profile?.user_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || profile.user_name || '');
      setBio(profile.bio || '');
      setAvatarUrl(profile.avatar_url || '');
    }
  }, [profile]);

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        display_name: displayName.trim(),
        bio: bio.trim(),
        avatar_url: avatarUrl.trim() || null,
      });
      onClose();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && !isSaving && onClose()}
        data-testid="edit-profile-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Edit Profile</h2>
                <p className="text-sm text-zinc-400">Update your public profile</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Avatar Preview */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center overflow-hidden">
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt="Avatar" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <User className={`w-12 h-12 text-white ${avatarUrl ? 'hidden' : ''}`} />
                </div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-zinc-800 border-2 border-zinc-900 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-zinc-400" />
                </div>
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Display Name *
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
                maxLength={50}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors"
                data-testid="edit-display-name"
              />
              <p className="text-xs text-zinc-500 mt-1">{displayName.length}/50 characters</p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Bio
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell others about yourself and your music..."
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors resize-none"
                data-testid="edit-bio"
              />
              <p className="text-xs text-zinc-500 mt-1">{bio.length}/500 characters</p>
            </div>

            {/* Avatar URL */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Avatar URL
              </label>
              <input
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://example.com/your-avatar.jpg"
                className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-500 transition-colors"
                data-testid="edit-avatar-url"
              />
              <p className="text-xs text-zinc-500 mt-1">Enter a URL to an image (JPG, PNG)</p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-zinc-800 flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isSaving}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving || !displayName.trim()}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="save-profile-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Trust Level Badge Component
const TrustBadge = ({ level }) => {
  const badges = {
    new: {
      icon: Shield,
      label: 'New Member',
      className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    },
    verified: {
      icon: ShieldCheck,
      label: 'Verified',
      className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    },
    trusted: {
      icon: Award,
      label: 'Trusted Creator',
      className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    },
    admin: {
      icon: ShieldAlert,
      label: 'Admin',
      className: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    },
  };

  const badge = badges[level] || badges.new;
  const Icon = badge.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${badge.className}`}>
      <Icon className="w-4 h-4" />
      {badge.label}
    </div>
  );
};

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color = 'violet' }) => {
  const colorClasses = {
    violet: 'from-violet-500/20 to-violet-600/10 text-violet-400',
    amber: 'from-amber-500/20 to-amber-600/10 text-amber-400',
    emerald: 'from-emerald-500/20 to-emerald-600/10 text-emerald-400',
    cyan: 'from-cyan-500/20 to-cyan-600/10 text-cyan-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 border border-zinc-800`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-zinc-900/50 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-zinc-400">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Preset Card Component (simplified version for profile)
const ProfilePresetCard = ({ preset, onViewDetails, onDownload, isOwnProfile }) => {
  const [isHovered, setIsHovered] = useState(false);

  const statusColors = {
    approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden cursor-pointer group relative"
      onClick={() => onViewDetails(preset)}
      data-testid={`profile-preset-${preset.id}`}
    >
      {/* Header with type badge */}
      <div className="relative h-20 bg-gradient-to-br from-violet-600/20 to-rose-600/20 flex items-center justify-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          preset.preset_type === 'master' 
            ? 'bg-gradient-to-br from-amber-500 to-orange-600' 
            : 'bg-gradient-to-br from-cyan-500 to-blue-600'
        }`}>
          {preset.preset_type === 'master' ? (
            <Sliders className="w-6 h-6 text-white" />
          ) : (
            <Music className="w-6 h-6 text-white" />
          )}
        </div>
        
        {/* Type badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
          preset.preset_type === 'master'
            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
            : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
        }`}>
          {preset.preset_type === 'master' ? 'Master' : 'Track'}
        </div>

        {/* Status badge (only for own profile) */}
        {isOwnProfile && preset.status && (
          <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[preset.status]}`}>
            {preset.status.charAt(0).toUpperCase() + preset.status.slice(1)}
          </div>
        )}

        {/* Featured badge */}
        {preset.is_featured && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-500/20 text-violet-400 border border-violet-500/30 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Featured
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-white truncate mb-1" title={preset.name}>
          {preset.name}
        </h3>
        <p className="text-xs text-zinc-500 mb-3">
          {new Date(preset.created_at).toLocaleDateString()}
        </p>
        
        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3">
            {/* Rating */}
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span className="text-white">{preset.average_rating?.toFixed(1) || '0.0'}</span>
            </div>
            
            {/* Upvotes */}
            <div className="flex items-center gap-1 text-zinc-400">
              <ArrowUp className="w-4 h-4" />
              <span>{preset.upvote_count || 0}</span>
            </div>
          </div>
          
          {/* Downloads */}
          <div className="flex items-center gap-1 text-zinc-500">
            <Download className="w-4 h-4" />
            <span>{preset.download_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Hover overlay */}
      <AnimatePresence>
        {isHovered && preset.status === 'approved' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-zinc-900/80 flex items-center justify-center"
          >
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onDownload(preset.id);
              }}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid={`download-profile-preset-${preset.id}`}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main User Profile Page
export const UserProfilePage = () => {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Use param userId or logged-in user's id for /profile route
  const userId = paramUserId || user?.id;
  
  // State
  const [profile, setProfile] = useState(null);
  const [presets, setPresets] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'track', 'master', 'pending', 'purchases', 'sales'
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [showEditModal, setShowEditModal] = useState(false);

  // Check if viewing own profile
  const isOwnProfile = user?.id === userId;

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/marketplace/users/${userId}/profile`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error('User not found');
        navigate('/marketplace');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId, fetchProfile]);

  // Fetch presets
  const fetchPresets = useCallback(async () => {
    setPresetsLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab === 'track' || activeTab === 'master') {
        params.append('preset_type', activeTab);
      }
      if (isOwnProfile && activeTab === 'pending') {
        params.append('status', 'pending');
        params.append('include_private', 'true');
      } else if (isOwnProfile) {
        params.append('include_private', 'true');
      }
      params.append('sort_by', sortBy);
      params.append('page', page.toString());
      params.append('limit', '12');

      const response = await fetch(`${API_URL}/api/marketplace/users/${userId}/presets?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    } finally {
      setPresetsLoading(false);
    }
  }, [userId, activeTab, sortBy, page, isOwnProfile]);

  // Fetch purchase history
  const fetchPurchases = useCallback(async () => {
    if (!userId || !isOwnProfile) return;
    
    try {
      const response = await fetch(`${API_URL}/api/payments/purchases/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPurchases(data.purchases || []);
      }
    } catch (error) {
      console.error('Failed to fetch purchases:', error);
    }
  }, [userId, isOwnProfile]);

  // Fetch sales history
  const fetchSales = useCallback(async () => {
    if (!userId || !isOwnProfile) return;
    
    try {
      const response = await fetch(`${API_URL}/api/payments/sales/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setSales(data.sales || []);
      }
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    }
  }, [userId, isOwnProfile]);

  useEffect(() => {
    if (userId) {
      fetchPresets();
      if (isOwnProfile) {
        fetchPurchases();
        fetchSales();
      }
    }
  }, [fetchPresets, fetchPurchases, fetchSales, userId, isOwnProfile]);

  // Reset page when tab changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, sortBy]);

  // Handle save profile
  const handleSaveProfile = async (profileData) => {
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/users/${userId}/profile`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(profileData),
        }
      );

      if (response.ok) {
        toast.success('Profile updated successfully');
        // Refresh profile data
        fetchProfile();
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Save profile error:', error);
      toast.error('Failed to save profile');
      throw error;
    }
  };

  // Handle download
  const handleDownload = async (presetId) => {
    if (!user) {
      toast.error('Please log in to download presets');
      navigate('/');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${presetId}/download?user_id=${user.id}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('importedPreset', JSON.stringify(data));
        toast.success('Preset downloaded! Open it in the studio to apply.');
        navigate('/studio');
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download preset');
    }
  };

  // Handle view details
  const handleViewDetails = (preset) => {
    navigate(`/marketplace/${preset.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-violet-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Profile Not Found</h2>
          <p className="text-zinc-400 mb-4">This user doesn't exist or hasn't shared any presets.</p>
          <Button onClick={() => navigate('/marketplace')} className="bg-violet-600 hover:bg-violet-500">
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900/50 border-b border-zinc-800 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/marketplace')}
              className="text-zinc-400 hover:text-white"
              data-testid="back-to-marketplace"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <User className="w-5 h-5 text-violet-500" />
                {isOwnProfile ? 'My Profile' : 'User Profile'}
              </h1>
              <p className="text-sm text-zinc-400">
                {isOwnProfile ? 'Manage your shared presets' : 'View shared presets'}
              </p>
            </div>
            
            {isOwnProfile && (
              <div className="ml-auto">
                <Button
                  onClick={() => navigate('/marketplace/upload')}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="upload-new-preset"
                >
                  <Music className="w-4 h-4 mr-2" />
                  Share New Preset
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            {/* Avatar & Basic Info */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center overflow-hidden">
                {profile.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt={profile.user_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <User className="w-10 h-10 text-white" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold text-white" data-testid="profile-username">
                    {profile.user_name}
                  </h2>
                  {isOwnProfile && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="p-1.5 text-zinc-400 hover:text-violet-400 hover:bg-zinc-800 rounded-lg transition-colors"
                      title="Edit Profile"
                      data-testid="edit-profile-btn"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <TrustBadge level={profile.trust_level} />
                {profile.member_since && (
                  <p className="text-sm text-zinc-500 mt-2 flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Member since {new Date(profile.member_since).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 md:ml-auto">
              <StatCard
                icon={Music}
                label="Presets"
                value={profile.stats.total_presets}
                color="violet"
              />
              <StatCard
                icon={Download}
                label="Downloads"
                value={profile.stats.total_downloads}
                color="cyan"
              />
              <StatCard
                icon={ArrowUp}
                label="Upvotes"
                value={profile.stats.total_upvotes}
                color="emerald"
              />
              <StatCard
                icon={Star}
                label="Avg Rating"
                value={profile.stats.average_rating?.toFixed(1) || '0.0'}
                color="amber"
              />
            </div>
          </div>

          {/* Bio section */}
          {profile.bio && (
            <div className="mt-6 p-4 bg-zinc-800/30 rounded-xl">
              <p className="text-zinc-300 text-sm leading-relaxed" data-testid="profile-bio">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Pending presets notice (own profile only) */}
          {isOwnProfile && profile.stats.pending_presets > 0 && (
            <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400" />
              <div>
                <p className="text-amber-400 font-medium">
                  {profile.stats.pending_presets} preset{profile.stats.pending_presets > 1 ? 's' : ''} pending review
                </p>
                <p className="text-sm text-zinc-400">
                  Your presets will be published after moderation
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Tabs & Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Tabs */}
          <div className="flex gap-1 bg-zinc-900/50 rounded-lg p-1 border border-zinc-800">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'all' ? 'bg-violet-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-all"
            >
              All Presets
            </button>
            <button
              onClick={() => setActiveTab('track')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'track' ? 'bg-cyan-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-track"
            >
              Track
            </button>
            <button
              onClick={() => setActiveTab('master')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'master' ? 'bg-amber-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="tab-master"
            >
              Master
            </button>
            {isOwnProfile && (
              <>
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'pending' ? 'bg-amber-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="tab-pending"
                >
                  <Clock className="w-4 h-4" />
                  Pending
                </button>
                <button
                  onClick={() => setActiveTab('purchases')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'purchases' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="tab-purchases"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Purchases
                </button>
                <button
                  onClick={() => setActiveTab('sales')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    activeTab === 'sales' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                  data-testid="tab-sales"
                >
                  <DollarSign className="w-4 h-4" />
                  Sales
                </button>
              </>
            )}
          </div>

          {/* Sort & View */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500"
                data-testid="sort-select"
              >
                <option value="newest">Newest</option>
                <option value="popular">Most Popular</option>
                <option value="top_rated">Top Rated</option>
              </select>
            </div>

            <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-zinc-700 text-white' : 'text-zinc-400'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Presets Grid */}
        {activeTab === 'purchases' && isOwnProfile ? (
          // Purchase History
          <div className="space-y-4">
            {purchases.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <ShoppingBag className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-zinc-400 mb-2">No purchases yet</h3>
                <p className="text-zinc-500 mb-4">Presets you buy will appear here</p>
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Music className="w-4 h-4 mr-2" />
                  Browse Marketplace
                </Button>
              </motion.div>
            ) : (
              purchases.map((purchase, idx) => (
                <motion.div
                  key={purchase.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{purchase.preset_name}</h3>
                        <p className="text-sm text-zinc-400">From: {purchase.creator_name}</p>
                        <p className="text-xs text-zinc-500">
                          {new Date(purchase.purchased_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-400">${purchase.amount.toFixed(2)}</div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/marketplace/${purchase.preset_id}`)}
                        className="text-zinc-400 hover:text-white"
                      >
                        View Preset
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : activeTab === 'sales' && isOwnProfile ? (
          // Sales History
          <div className="space-y-4">
            {sales.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <DollarSign className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-zinc-400 mb-2">No sales yet</h3>
                <p className="text-zinc-500 mb-4">When someone buys your preset, it will appear here</p>
                <Button
                  onClick={() => navigate('/marketplace/upload')}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  <Music className="w-4 h-4 mr-2" />
                  Upload a Preset
                </Button>
              </motion.div>
            ) : (
              sales.map((sale, idx) => (
                <motion.div
                  key={sale.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{sale.preset_name}</h3>
                        <p className="text-sm text-zinc-400">Buyer: {sale.buyer_name}</p>
                        <p className="text-xs text-zinc-500">
                          {new Date(sale.purchased_at).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'short', day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-emerald-400">+${sale.creator_earnings.toFixed(2)}</div>
                      <div className="text-xs text-zinc-500">Sale: ${sale.amount.toFixed(2)}</div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        ) : presetsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-zinc-900/50 border border-zinc-800 rounded-xl h-48 animate-pulse" />
            ))}
          </div>
        ) : presets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <Music className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">
              {activeTab === 'pending' 
                ? 'No pending presets' 
                : isOwnProfile 
                  ? "You haven't shared any presets yet"
                  : 'No presets found'}
            </h3>
            <p className="text-zinc-500 mb-4">
              {isOwnProfile && activeTab !== 'pending'
                ? 'Share your first preset to start building your profile!'
                : 'Check back later for new presets'}
            </p>
            {isOwnProfile && (
              <Button
                onClick={() => navigate('/marketplace/upload')}
                className="bg-violet-600 hover:bg-violet-500"
              >
                <Music className="w-4 h-4 mr-2" />
                Share Your First Preset
              </Button>
            )}
          </motion.div>
        ) : (
          <>
            <div className={`grid gap-4 ${
              viewMode === 'grid'
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1 md:grid-cols-2'
            }`}>
              {presets.map((preset) => (
                <ProfilePresetCard
                  key={preset.id}
                  preset={preset}
                  onViewDetails={handleViewDetails}
                  onDownload={handleDownload}
                  isOwnProfile={isOwnProfile}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-zinc-400"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </Button>
                <span className="text-sm text-zinc-400">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-zinc-400"
                >
                  Next
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        profile={profile}
        onSave={handleSaveProfile}
      />
    </div>
  );
};

export default UserProfilePage;
